// Library
import React, {
  useLayoutEffect,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useContext,
  useState,
} from "react";
import clsx from "clsx";
import {
  NavLink,
  useLocation,
  matchPath,
  withRouter,
  useHistory,
} from "react-router-dom";

//Components
import {
  AppBar,
  Button,
  CssBaseline,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Modal,
  Popover,
  Toolbar,
  Tooltip,
  Typography,
  Box,
} from "@material-ui/core";
import NotificationsDropdown from "../Notifications/NotificationDropDown/NotificationsDropdown.component";
import NotificationModal from "../Notifications/NotificationModal/NotificationModal";
import ProfileDropdown from "../ProfileDropdown/ProfileDropdown.component";
import {
  MovieColabLogo,
  MovieColabLogoCustom,
} from "../Utilities/MovieColabLogo";
import ExternalReviewerSidebar from "./ExternalReviewerSideBar/ExternalReviewerSidebar";
import NoTeamAdminModel from "./NoTeamAdminModel/NoTeamAdminModel";

// Styles
import useStyles from "./SidebarAndNavbar.styles";

//icons
import { Bell, ChevronDown, User, MessageCircle, Home } from "react-feather";

import {
  AlignLeft,
  Bookmark,
  BookmarkSimple,
  Browsers,
  Cards,
  CheckSquareOffset,
  CirclesThreePlus,
  Clock,
  Cube,
  FilmSlate,
  FilmStrip,
  Folders,
  FolderSimpleUser,
  FolderStar,
  Gear,
  GearSix,
  Hexagon,
  House,
  Info,
  Queue,
  Rectangle,
  Shield,
  Sparkle,
  Stack,
  Tag,
  Triangle,
  UserFocus,
  UserList,
  UsersThree,
  Warning,
} from "phosphor-react";
import { EpisodesIconCustom } from "../Utilities/Svg/EpisodesIcon";
import { ProjectMediaIconCustom } from "../Utilities/Svg/ProjectMediaIcon";
import { fget } from "../../API/callsAPI";
import { MovieColabContext } from "../../Context/MovieColabContext";
import StringTruncate from "../Utilities/StringTruncate/StringTruncate";
import { ReviewerIcon, ReviewerIconSidebar } from "../Utilities/Svg/Reviewer";
import BellAnimation from "./BellAnimation";
import { BASE_URL_ADMIN } from "../../API/callsAPI";
import { isChatEnabledForProject } from "../ChatService/utils/chatConfig";
import { ButtonGroup, IconButton } from "@mui/material";
import BellIcon from "../Utilities/Svg/BellIcon";
import { AdminIcon, UserIcon } from "../Utilities/Svg/UserRoleIcons";

// Lazy load chat drawer
const ChatDrawer = React.lazy(() =>
  import("../ChatService/ChatDrawer/ChatDrawer.component")
);

const calculateNavOverflow = (itemWidths, containerWidth, moreButtonWidth) => {
  if (!itemWidths.length) {
    return 0;
  }

  const totalWidth = itemWidths.reduce((sum, width) => sum + width, 0);
  if (totalWidth <= containerWidth) {
    return itemWidths.length;
  }

  let usedWidth = 0;
  let lastFitIndex = -1;

  for (let i = 0; i < itemWidths.length; i += 1) {
    if (usedWidth + itemWidths[i] + moreButtonWidth <= containerWidth) {
      usedWidth += itemWidths[i];
      lastFitIndex = i;
    } else {
      break;
    }
  }

  return Math.max(0, lastFitIndex + 1);
};

const PAGE_ROUTES = {
  updates: "updates",
  tasks: "tasks",
  assets: "assets",
  sequences: "sequence",
  sequence: "sequence",
  episodes: "episodes",
  shots: "shots-all",
  reviews: "review",
  review: "review",
  custom_pages: "custom-page",
  customPage: "custom-page",
  project_media: "project-media",
  projectMedia: "project-media",
  playlist: "playlist",
  department: "departments",
  departments: "departments",
};

const PAGE_ICONS = {
  updates: Clock,
  tasks: CheckSquareOffset,
  assets: Cube,
  episodes: EpisodesIconCustom,
  sequences: FilmStrip,
  shots: FilmSlate,
  reviews: ReviewerIcon,
  custom_pages: Cards,
  project_media: ProjectMediaIconCustom,
  playlist: FolderStar,
};

const GROUP_ICON_OPTIONS = [
  { id: "circles-three-plus", icon: CirclesThreePlus },
  { id: "folders", icon: Folders },
  { id: "stack", icon: Stack },
  { id: "align-left", icon: AlignLeft },
  { id: "user-list", icon: UserList },
  { id: "user-focus", icon: UserFocus },
  { id: "folder-simple-user", icon: FolderSimpleUser },
  { id: "browsers", icon: Browsers },
  { id: "bookmark-simple", icon: BookmarkSimple },
  { id: "bookmark", icon: Bookmark },
  { id: "tag", icon: Tag },
  { id: "triangle", icon: Triangle },
  { id: "rectangle", icon: Rectangle },
  { id: "hexagon", icon: Hexagon },
];

const getGroupIcon = (iconId) =>
  GROUP_ICON_OPTIONS.find((option) => option.id === iconId)?.icon ||
  CirclesThreePlus;

const getNavItemIcon = (item) => {
  if (item.type === "group") {
    return getGroupIcon(item.icon);
  }
  if (item.type === "customPage") {
    return Cards;
  }
  return PAGE_ICONS[item.key] || null;
};

const NavItemIcon = ({ icon: Icon, isSelected = false, size = 16 }) => {
  if (!Icon) {
    return null;
  }

  return (
    <Icon
      size={size}
      color={isSelected ? "#F9F9F9" : "#B1B5C9"}
      weight={isSelected ? "fill" : "regular"}
    />
  );
};

const getNavItemStartIcon = (Icon, isSelected = false) =>
  Icon ? <NavItemIcon icon={Icon} isSelected={isSelected} /> : undefined;

const matchesNavPath = (pathname, path, exact = true) =>
  Boolean(matchPath(pathname, { path, exact }));

const getPageRoute = (item, projectId) => {
  if (!item || !projectId) {
    return "/";
  }

  if (item.type === "customPage") {
    return `/${projectId}/custom-page/${item.key}`;
  }

  const path = PAGE_ROUTES[item.key];
  return path ? `/${projectId}/${path}` : `/${projectId}/${item.key}`;
};

const mapConfigItemToNavItem = (item, projectId) => {
  if (item.type === "group") {
    const children = (item.items || [])
      .filter((child) => child.enabled !== false)
      .map((child) => {
        const to = getPageRoute(child, projectId);
        return {
          id: child.key,
          label: child.label,
          to,
          Icon: getNavItemIcon(child),
          match: (pathname) => matchesNavPath(pathname, to),
        };
      });

    return {
      id: item.key,
      type: "group",
      label: item.name || item.label,
      children,
      Icon: getNavItemIcon(item),
      match: (pathname) => children.some((child) => child.match(pathname)),
    };
  }

  const to = getPageRoute(item, projectId);
  return {
    id: item.key,
    type: "link",
    to,
    label: item.label,
    Icon: getNavItemIcon(item),
    match: (pathname) => matchesNavPath(pathname, to),
  };
};

const getNavItemButtonClassName = (classes, isSelected, showPageNames) =>
  clsx(
    classes.navButton,
    isSelected && classes.navButtonSelected,
    !showPageNames && classes.navButtonIconOnly
  );

const NavBarItem = ({
  item,
  isSelected = false,
  measureOnly = false,
  showPageNames = true,
  onGroupMouseEnter,
}) => {
  const classes = useStyles();
  const startIcon = showPageNames
    ? getNavItemStartIcon(item.Icon, isSelected)
    : undefined;
  const content = showPageNames ? (
    item.label
  ) : (
    <NavItemIcon icon={item.Icon} isSelected={isSelected} />
  );
  const buttonClassName = getNavItemButtonClassName(
    classes,
    isSelected,
    showPageNames
  );

  const itemNode = (
    <div className={classes.navItemWrapper}>
      {item.type === "link" ? (
        measureOnly ? (
          <Button
            disableElevation
            className={buttonClassName}
            startIcon={startIcon}
          >
            {content}
          </Button>
        ) : (
          <NavLink to={item.to} className={classes.navLink}>
            <Button
              disableElevation
              className={buttonClassName}
              startIcon={startIcon}
            >
              {content}
            </Button>
          </NavLink>
        )
      ) : (
        <Button
          disableElevation
          className={buttonClassName}
          startIcon={startIcon}
          onMouseEnter={
            measureOnly
              ? undefined
              : (event) => onGroupMouseEnter?.(event, item)
          }
        >
          {content}
        </Button>
      )}
    </div>
  );

  if (!showPageNames && !measureOnly) {
    return (
      <Tooltip title={item.label} placement="bottom">
        {itemNode}
      </Tooltip>
    );
  }

  return itemNode;
};

const NavBarMoreButton = ({ onClick }) => {
  const classes = useStyles();

  return (
    <div className={classes.navItemWrapper}>
      <Button disableElevation className={classes.moreButton} onClick={onClick}>
        More
      </Button>
    </div>
  );
};

function NavbarAndSidebar(props) {
  const { selectedProject, userRoleForProject } = props;
  //States
  const location = useLocation();
  const history = useHistory();
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [openModal, setModalOpen] = useState(false);
  const [openNotificationsDropDownModal, setopenNotificationsDropDownModal] =
    useState(false);
  const [isNotificationModalOpen, setNotificationModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const [currentProject, setCurrentProject] = useState({});
  //state that updates sidebar
  const [sidebarType, setSidebarType] = useState("internal"); //internal || external
  const [subFilterNotification, setSubFilterNotification] = useState({
    projectId: "",
    subFilterName: "",
  });
  const [isFailedAdminModalOpen, setIsFailedAdminModalOpen] = useState(false);
  const [isChatDrawerOpen, setIsChatDrawerOpen] = useState(false);
  const [isChatEnabled, setIsChatEnabled] = useState(false);
  const {
    loggedInUser,
    notifications,
    isNewNotificationAvailable,
    handleNewNotificationStatus,
    myTeams,
    navConfig,
    getNavConfig,
  } = useContext(MovieColabContext);
  const navAreaRef = useRef(null);
  const measureRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(0);
  const [moreAnchorEl, setMoreAnchorEl] = useState(null);

  useEffect(() => {
    if (location.pathname.includes("/task")) {
      document.title = `${props.selectedProject?.name}-task`;
    } else if (
      document.title !== "MovieColab" &&
      !location.pathname.includes("/shotversions") &&
      !location.pathname.includes("/assetversions")
    ) {
      document.title = "MovieColab";
    }
  }, [location, props.selectedProject]);

  useEffect(() => {
    if (selectedProject.role === "reviewer") {
      setSidebarType("external");
    } else {
      setSidebarType("internal");
    }
    // Check if chat is enabled for this project's organization
    setIsChatEnabled(isChatEnabledForProject(props.selectedProject));
    return () => {};
  }, [selectedProject.role, props.selectedProject]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    handleNewNotificationStatus(false);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleModelToggle = () => {
    setModalOpen(!openModal);
  };
  const handleNotificationsModalToggle = () => {
    setopenNotificationsDropDownModal(!openNotificationsDropDownModal);
  };

  const handleNotificationModal = () => {
    handleClose();
    setNotificationModalOpen(!isNotificationModalOpen);
  };

  const handleChatDrawerToggle = () => {
    setIsChatDrawerOpen(!isChatDrawerOpen);
  };

  const { pathname } = props.location;

  const isPathHome = pathname === "/";
  const isTeams = pathname.includes("/my-teams");
  const isNotification = pathname === "/notifications";
  const isReports = pathname === "/reports";
  const isPdf = pathname === "/pdf-reports";
  const isSettingsSelected = Boolean(
    selectedProject?.id &&
    matchesNavPath(pathname, `/${selectedProject.id}/settings`, false),
  );
  const TIMEOUT = 3000;
  let timer;
  function mouseEnter() {
    timer = setTimeout(() => {
      setOpen(true);
    }, TIMEOUT);
  }

  function mouseLeave() {
    clearTimeout(timer);
    setOpen(false);
  }

  const openpopover = Boolean(anchorEl);
  const id = openpopover ? "simple-popover" : undefined;

  const [recallUnreadNotification, setRecallUnreadNotification] = useState(0);
  const [unreadNotificationData, setUnreadNotificationData] = useState({
    isAvailable: false,
    count: 0,
  });

  const getUnreadNotificationCount = async () => {
    try {
      const res = await fget({ url: "unread-count/" });
      if (res?.data?.unread_count > 0) {
        setUnreadNotificationData({
          isAvailable: true,
          count: res?.data?.unread_count,
        });
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getUnreadNotificationCount();
    setTimeout(() => {
      setRecallUnreadNotification(recallUnreadNotification + 1);
    }, 300000);
  }, [recallUnreadNotification]);
  const [openGroupPopover, setOpenGroupPopover] = useState(null);

  useEffect(() => {
    if (!selectedProject?.id) {
      return;
    }
    getNavConfig(selectedProject.id);
  }, [selectedProject?.id, getNavConfig]);

  const mappedNavItems = useMemo(
    () =>
      (navConfig?.config?.items || [])
        .filter((item) => item.enabled)
        .map((item) => mapConfigItemToNavItem(item, selectedProject.id)),
    [navConfig, selectedProject.id]
  );

  const showPageNames = navConfig?.config?.show_page_names ?? true;

  const handleClickGroup = useCallback((event, item) => {
    setOpenGroupPopover({
      anchorEl: event.currentTarget,
      groupId: item.id,
    });
  }, []);

  const handleCloseGroup = useCallback(() => {
    setOpenGroupPopover(null);
  }, []);

  const handleOpenMore = useCallback((event) => {
    setMoreAnchorEl(event.currentTarget);
  }, []);

  const handleCloseMore = useCallback(() => {
    setMoreAnchorEl(null);
  }, []);

  const openGroupItem = mappedNavItems.find(
    (item) => item.id === openGroupPopover?.groupId
  );
  const groupChildren = openGroupItem?.children || [];

  const visibleNavItems = mappedNavItems.slice(0, visibleCount);
  const overflowNavItems = mappedNavItems.slice(visibleCount);

  useLayoutEffect(() => {
    const navArea = navAreaRef.current;
    const measurer = measureRef.current;

    if (!navArea || !measurer || !mappedNavItems.length) {
      setVisibleCount(mappedNavItems.length);
      return undefined;
    }

    const updateNavOverflow = () => {
      const availableWidth = navArea.getBoundingClientRect().width;
      if (!availableWidth) {
        return;
      }

      const measureElements = Array.from(measurer.children);
      const moreButtonWidth =
        measureElements[measureElements.length - 1]?.getBoundingClientRect()
          .width || 60;
      const itemWidths = measureElements
        .slice(0, -1)
        .map((child) => child.getBoundingClientRect().width);

      setVisibleCount(
        calculateNavOverflow(itemWidths, availableWidth, moreButtonWidth)
      );
    };

    updateNavOverflow();

    const resizeObserver = new ResizeObserver(updateNavOverflow);
    resizeObserver.observe(navArea);

    return () => resizeObserver.disconnect();
  }, [mappedNavItems, showPageNames]);

  return (
    <>
      {!isTeams ? (
        <div className={classes.root}>
          <CssBaseline />
          <AppBar position="fixed" className={classes.appBar}>
            <Toolbar
              style={{
                backgroundColor: "#252A38",
                minHeight: "55px",
                height: "55px",
                padding: "0px 24px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "20px",
                  height: "100%",
                  flex: "1 1 auto",
                  minWidth: 0,
                }}
              >
                {isPathHome ? (
                  <a href="/" style={{ textDecoration: "none" }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        cursor: "pointer",
                      }}
                    >
                      <MovieColabLogoCustom height={24} width={35.5} />
                      <Typography className={classes.title}>
                        Movie Colab
                      </Typography>
                    </div>
                  </a>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <img
                      src={selectedProject?.thumbnail}
                      height={36}
                      width={36}
                      style={{ borderRadius: "4.6px" }}
                    />
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        height: "100%",
                        minWidth: "max-content",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {selectedProject?.name}
                      </Typography>
                      <Typography
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "5px",
                          fontSize: "12px",
                          color: "#B1B5C9",
                        }}
                      >
                        {selectedProject.role === "admin" ? (
                          <AdminIcon height={14} width={14} />
                        ) : selectedProject.role === "user" ? (
                          <UserIcon height={14} width={14} />
                        ) : selectedProject.role === "reviewer" ? (
                          <ReviewerIcon height={14} width={14} />
                        ) : (
                          <></>
                        )}
                        {selectedProject.role}
                      </Typography>
                    </div>
                  </div>
                )}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    height: "100%",
                    flex: "1 1 auto",
                    minWidth: 0,
                    overflow: "hidden",
                  }}
                >
                  <NavLink
                    to={"/"}
                    style={{ textDecoration: "none", height: "100%" }}
                  >
                    <Button
                      disableElevation
                      style={{
                        backgroundColor:
                          location.pathname === "/" ? "#1B1D28" : "transparent",
                        color:
                          location.pathname === "/" ? "#F9F9F9" : "#B1B5C9",
                        borderRadius: "0px",
                        boxShadow: "none",
                        padding: "10px 12px",
                        height: "100%",
                        whiteSpace: "nowrap",
                        minWidth: "max-content",
                      }}
                    >
                      <Home size={16} weight={"regular"} />
                    </Button>
                  </NavLink>
                  {!isPathHome && !isReports && (
                    <NavLink
                      to={`/${selectedProject.id}/settings/notifications`}
                      style={{ textDecoration: "none", height: "100%" }}
                    >
                      <Button
                        disableElevation
                        style={{
                          backgroundColor: isSettingsSelected
                            ? "#1B1D28"
                            : "transparent",
                          color: isSettingsSelected ? "#F9F9F9" : "#B1B5C9",
                          borderRadius: "0px",
                          boxShadow: "none",
                          padding: "10px 12px",
                          height: "100%",
                          whiteSpace: "nowrap",
                          minWidth: "max-content",
                        }}
                      >
                        <Gear
                          size={16}
                          weight={isSettingsSelected ? "fill" : "regular"}
                        />
                      </Button>
                    </NavLink>
                  )}

                  {!isPathHome && !isReports && (
                    <>
                      <div
                        ref={navAreaRef}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          height: "100%",
                          flex: "1 1 auto",
                          minWidth: 0,
                          overflow: "hidden",
                          marginLeft: "8px",
                        }}
                      >
                        <div
                          ref={measureRef}
                          aria-hidden="true"
                          className={classes.hiddenMeasure}
                        >
                          {mappedNavItems.map((item) => (
                            <NavBarItem
                              key={item.id}
                              item={item}
                              measureOnly
                              showPageNames={showPageNames}
                            />
                          ))}
                          <NavBarMoreButton />
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            height: "100%",
                            minWidth: 0,
                            overflow: "hidden",
                          }}
                        >
                          {visibleNavItems.map((item) => (
                            <NavBarItem
                              key={item.id}
                              item={item}
                              isSelected={item.match?.(location.pathname)}
                              showPageNames={showPageNames}
                              onGroupMouseEnter={handleClickGroup}
                            />
                          ))}
                          {overflowNavItems.length > 0 && (
                            <NavBarMoreButton onClick={handleOpenMore} />
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                {!isPathHome && !isReports && (
                  <IconButton
                    sx={{
                      height: "34px",
                      width: "34px",
                      borderRadius: "5px",
                      padding: "8px",
                      background:
                        "linear-gradient(270deg, #6C65D9 0%, #839CFF 100%)",
                      color: "#F9F9F9",
                      "&:hover": {
                        background:
                          "linear-gradient(270deg, #7B72E8 0%, #91A8FF 100%)",
                        boxShadow: "0 4px 12px rgba(108, 101, 217, 0.25)",
                      },
                    }}
                    onClick={handleChatDrawerToggle}
                  >
                    <Sparkle size={18} color="#F9F9F9" weight="fill" />
                  </IconButton>
                )}
                <IconButton
                  sx={{
                    height: "34px",
                    width: "34px",
                    borderRadius: "5px",
                    padding: "8px",
                    border: "1px solid #6C65D9",
                    backgroundColor: "transparent",
                    color: "#F9F9F9",
                    "&:hover": {
                      backgroundColor: "rgba(108, 101, 217, 0.16)",
                      borderColor: "#839CFF",
                      boxShadow: "0 4px 10px rgba(108, 101, 217, 0.2)",
                    },
                  }}
                  onClick={() =>
                    myTeams.length > 0
                      ? window.open(BASE_URL_ADMIN, "_blank")
                      : setIsFailedAdminModalOpen(true)
                  }
                >
                  <Shield size={18} color="#F9F9F9" />
                </IconButton>
                <IconButton
                  sx={{
                    height: "34px",
                    width: "34px",
                    borderRadius: "5px",
                    padding: "8px",
                    border: "1px solid #6C65D9",
                    backgroundColor: "transparent",
                    color: "#F9F9F9",
                    "&:hover": {
                      backgroundColor: "rgba(108, 101, 217, 0.16)",
                      borderColor: "#839CFF",
                      boxShadow: "0 4px 10px rgba(108, 101, 217, 0.2)",
                    },
                  }}
                  onClick={() => history.push("/my-teams")}
                >
                  <UsersThree size={18} color="#F9F9F9" />
                </IconButton>
                <Button
                  className={classes.topBarIcon}
                  aria-describedby={
                    Boolean(anchorEl) ? "simple-popover" : undefined
                  }
                  onClick={handleNotificationModal}
                  // onMouseEnter={(event) => setAnchorEl(event.currentTarget)}
                  onMouseEnter={(event) => {
                    setTimeout(() => {
                      setAnchorEl(event.currentTarget);
                    }, 2000);
                  }}
                  // onMouseLeave={handleClose}
                >
                  {unreadNotificationData?.isAvailable ? (
                    <div className={classes.bellIconAnimated}>
                      <span className={classes.animatedSpan}>
                        {unreadNotificationData?.count + 1}
                      </span>
                      <BellAnimation />
                    </div>
                  ) : isNewNotificationAvailable && notifications?.length ? (
                    <BellIcon />
                  ) : (
                    <Bell />
                  )}
                </Button>
                <Popover
                  id={Boolean(anchorEl) ? "simple-popover" : undefined}
                  open={Boolean(anchorEl)}
                  anchorEl={anchorEl}
                  onClose={handleClose}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  PaperProps={{
                    style: {
                      backgroundColor: "transparent",
                      border: "none",
                      boxShadow: "none",
                    },
                  }}
                >
                  <div className={classes.popoverArrowTriangle}></div>
                  <div style={{ position: "relative" }}>
                    <div className={classes.popoverArrow}></div>
                    <NotificationsDropdown
                      handleNotificationsModalToggle={
                        handleNotificationsModalToggle
                      }
                      handleNotificationModal={handleNotificationModal}
                    />
                  </div>
                </Popover>
                <Button
                  className={classes.topBarIcon}
                  onClick={handleModelToggle}
                >
                  {loggedInUser?.avatar ? (
                    <img
                      src={loggedInUser?.avatar}
                      alt="avatar"
                      className={classes.userNameAvatar}
                    />
                  ) : (
                    <div className={classes.userNameAvatar}>
                      {loggedInUser?.first_name?.charAt(0).toUpperCase() ?? ""}
                      {loggedInUser?.last_name?.charAt(0).toUpperCase() ?? ""}
                    </div>
                  )}
                  <ChevronDown />
                </Button>
              </div>
              <Popover
                id={Boolean(openGroupPopover) ? "simple-popover" : undefined}
                open={Boolean(openGroupPopover)}
                anchorEl={openGroupPopover?.anchorEl}
                onClose={handleCloseGroup}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                PaperProps={{
                  style: {
                    backgroundColor: "#252A38",
                    borderRadius: "5px",
                    border: "1px solid #606479",
                    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.24)",
                    padding: "0px",
                  },
                }}
              >
                <Box
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "6px",
                    minWidth: "180px",
                  }}
                  onMouseLeave={handleCloseGroup}
                >
                  {groupChildren.map((child) => (
                    <Button
                      key={child.id}
                      component={NavLink}
                      to={child.to}
                      onClick={handleCloseGroup}
                      fullWidth
                      style={{
                        justifyContent: "flex-start",
                        color: "#F9F9F9",
                        textTransform: "none",
                        borderRadius: "6px",
                        padding: "10px",
                        backgroundColor: child.match?.(location.pathname)
                          ? "#1B1D28"
                          : "transparent",
                      }}
                      startIcon={getNavItemStartIcon(
                        child.Icon,
                        child.match?.(location.pathname)
                      )}
                    >
                      {child.label}
                    </Button>
                  ))}
                </Box>
              </Popover>
              <Popover
                open={Boolean(moreAnchorEl)}
                anchorEl={moreAnchorEl}
                onClose={handleCloseMore}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                PaperProps={{
                  style: {
                    backgroundColor: "#252A38",
                    borderRadius: "5px",
                    border: "1px solid #606479",
                    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.24)",
                    // padding: "6px",
                  },
                }}
              >
                <Box
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                    minWidth: "180px",
                  }}
                >
                  {overflowNavItems.map((item) => {
                    const isSelected = item.match?.(location.pathname);
                    return (
                      <div key={item.id}>
                        {item.type === "link" ? (
                          <Button
                            component={NavLink}
                            to={item.to}
                            onClick={handleCloseMore}
                            fullWidth
                            style={{
                              justifyContent: "flex-start",
                              color: "#F9F9F9",
                              textTransform: "none",
                              borderRadius: "6px",
                              padding: "10px",
                              backgroundColor: isSelected
                                ? "#1B1D28"
                                : "transparent",
                            }}
                            startIcon={getNavItemStartIcon(
                              item.Icon,
                              isSelected
                            )}
                          >
                            {item.label}
                          </Button>
                        ) : (
                          <>
                            <Typography
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                color: "#B1B5C9",
                                fontSize: "12px",
                                padding: "8px 10px 2px",
                              }}
                            >
                              <NavItemIcon icon={item.Icon} />
                              {item.label}
                            </Typography>
                            {item.children?.map((child) => (
                              <Button
                                key={child.id}
                                component={NavLink}
                                to={child.to}
                                onClick={handleCloseMore}
                                fullWidth
                                style={{
                                  justifyContent: "flex-start",
                                  color: "#F9F9F9",
                                  textTransform: "none",
                                  borderRadius: "6px",
                                  padding: "10px",
                                  backgroundColor: child.match?.(
                                    location.pathname
                                  )
                                    ? "#1B1D28"
                                    : "transparent",
                                }}
                                startIcon={getNavItemStartIcon(
                                  child.Icon,
                                  child.match?.(location.pathname)
                                )}
                              >
                                {child.label}
                              </Button>
                            ))}
                          </>
                        )}
                      </div>
                    );
                  })}
                </Box>
              </Popover>
            </Toolbar>
          </AppBar>

          {/* sidebar drawer isn't shown when its notification and reports page or projects page */}
          {/* render below sidebar if it is admin or normal user             */}

          {/* add external reviewer component here */}
          {selectedProject?.role === "reviewer" &&
            !isPathHome &&
            !isReports &&
            !isTeams && (
              <ExternalReviewerSidebar
                projectId={selectedProject.id}
                handleNotificationModal={handleNotificationModal}
                setSubFilterNotification={setSubFilterNotification}
              />
            )}

          <Modal
            open={openModal}
            onClose={handleModelToggle}
            aria-labelledby="simple-modal-title"
            aria-describedby="simple-modal-description"
          >
            <ProfileDropdown
              themeToggler={props.themeToggler}
              userInfo={props.userInfo}
              handleClose={handleModelToggle}
            />
          </Modal>
          <Modal
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
            open={isNotificationModalOpen}
            onClose={() => setNotificationModalOpen(false)}
          >
            {/* setNotificationModalOpen is passed to close the modal after redirecting to url */}
            <NotificationModal
              handleNotificationModal={handleNotificationModal}
              setNotificationModalOpen={setNotificationModalOpen}
              sidebarType={sidebarType}
              isPath={isPathHome}
              subFilterNotification={subFilterNotification}
              projectId={selectedProject.id}
            />
          </Modal>
          <Modal
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
            open={isFailedAdminModalOpen}
            onClose={() => setIsFailedAdminModalOpen(false)}
          >
            <NoTeamAdminModel
              handleClose={() => setIsFailedAdminModalOpen(false)}
              handleCreateTeam={() => history.push("/my-teams")}
            />
          </Modal>

          {/* Chat Drawer */}
          {isChatEnabled && (
            <React.Suspense fallback={<div />}>
              <ChatDrawer
                open={isChatDrawerOpen}
                onClose={handleChatDrawerToggle}
                projectId={selectedProject.id}
              />
            </React.Suspense>
          )}
          <main
            className={
              selectedProject?.role === "reviewer" && !isPathHome && !isReports
                ? classes.reviewerContent
                : classes.contentClose
            }
          >
            <div className={classes.toolbar} />
            {props.children}
          </main>
        </div>
      ) : (
        <main>{props.children}</main>
      )}
    </>
  );
}

export default withRouter(NavbarAndSidebar);
